import type { Account } from '@domain/finance'
import { sumCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { Glass } from '@ui/glass/Glass'
import { useDeviceFlag } from '@ui/shell/device-preference'

/**
 * O card de saldo total do Dashboard.
 *
 * ## Só CONTA, nunca cartão
 *
 * `Docs/04-Design/Telas/Dashboard.md` é explícito: *"Somar só contas, nunca
 * cartão — `CreditCard` não guarda dinheiro"*. O tipo já ajuda — este
 * componente só recebe `Account[]` —, mas a razão fica escrita porque somar o
 * limite disponível de um cartão ao saldo é um erro que parece certo.
 *
 * ## Conta ARQUIVADA fica de fora
 *
 * Arquivar é como uma conta sai dos seletores sem sumir do histórico. O saldo é
 * uma pergunta sobre agora, e uma conta encerrada com R$ 3,20 esquecidos
 * inflaria o número para sempre.
 *
 * ## Saldo `null` NÃO é zero, e a tela diz isso
 *
 * `Account.balanceCents` é nulo em conta `cash` por invariante do domínio — não
 * há como saber quanto há no bolso — e em conta manual que ninguém atualizou.
 * Somá-las como zero afirmaria que estão vazias. Elas ficam fora da soma e
 * viram uma linha dizendo quantas são: um total que esconde o que não sabe é
 * pior do que um total que declara o buraco.
 *
 * ## O olho começa ESCONDENDO
 *
 * A preferência vive no dispositivo (`useDeviceFlag`), e é lida num efeito —
 * então o primeiro quadro usa o padrão. Com o padrão em "mostrar", quem escolheu
 * esconder veria o saldo piscar na tela antes de sumir, que é exatamente o que a
 * preferência existe para impedir. O padrão é o lado seguro, e o efeito só
 * relaxa.
 */

/** Os dígitos borrados. Largura fixa para o card não pular ao alternar. */
const OCULTO = '••••••'

export function Saldo({ accounts }: { accounts: Account[] }) {
  const [escondido, setEscondido] = useDeviceFlag('saldo-escondido', true)

  const ativas = accounts.filter((account) => account.status === 'active')
  const comSaldo = ativas.filter((account) => account.balanceCents !== null)
  const semSaldo = ativas.length - comSaldo.length
  const total = sumCents(comSaldo.map((account) => account.balanceCents ?? 0))

  return (
    <Glass level="frost" className="mb-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 id="saldo-total" className="text-xs text-[var(--text-muted)]">
          Saldo total
        </h2>
        <button
          type="button"
          aria-pressed={escondido}
          className="alvo justify-start text-xs underline"
          onClick={() => {
            setEscondido(!escondido)
          }}
        >
          {escondido ? 'Mostrar saldo' : 'Esconder saldo'}
        </button>
      </div>

      <p aria-labelledby="saldo-total" className="text-3xl font-semibold tabular-nums">
        {escondido ? OCULTO : formatBRL(total)}
      </p>

      {ativas.length === 0 && (
        <p className="text-xs text-[var(--text-muted)]">Nenhuma conta cadastrada ainda.</p>
      )}
      {semSaldo > 0 && (
        <p className="text-xs text-[var(--text-muted)]">
          {semSaldo === 1
            ? '1 conta sem saldo conhecido, fora deste número.'
            : `${String(semSaldo)} contas sem saldo conhecido, fora deste número.`}
        </p>
      )}
    </Glass>
  )
}
